import React from 'react';
import Button from '../../../../components/ui/Button';
import {
  X,
  Users,
  Mail,
  Phone,
  Building2,
  MapPin,
  DollarSign,
  Calendar,
  AlertCircle,
  CheckCircle,
  Briefcase,
  User
} from 'lucide-react';

const DetailField = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-sm text-foreground">{value ?? 'N/A'}</p>
  </div>
);

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleString();
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  // Handle DD-MM-YYYY format
  const parts = dateString.split('-');
  if (parts.length === 3) {
    return dateString; // Return as is for DD-MM-YYYY
  }
  const parsed = new Date(dateString);
  return Number.isNaN(parsed.getTime()) ? dateString : parsed.toLocaleDateString();
};

const EmployeeDetailsModal = ({ isOpen, onClose, employee, isLoading, error }) => {
  if (!isOpen) return null;

  const empData = employee?.employee_data || {};
  const basicInfo = empData?.basic_information || {};
  const employmentInfo = empData?.employment_information || {};
  const personalInfo = empData?.personal_information || {};
  const presentAddress = empData?.present_address || {};
  const permanentAddress = empData?.permanent_address || {};
  const bankingDetails = empData?.banking_details || {};

  const statusBadge = (
    <span
      className={`px-3 py-1 rounded-full text-xs font-semibold ${
        employee?.is_active !== false
          ? 'bg-green-100 text-green-700'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {employee?.is_active !== false ? 'Active' : 'Inactive'}
    </span>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-full">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Employee Details</p>
                <h2 className="text-xl font-semibold text-foreground">
                  {basicInfo?.full_name || 'Employee'}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {employmentInfo?.designation || '—'} • {employmentInfo?.department || '—'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Created {formatDateTime(employee?.created_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>Updated {formatDateTime(employee?.updated_at)}</span>
              </div>
              {basicInfo?.employee_id && (
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span>Employee ID: {basicInfo.employee_id}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {statusBadge}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <p className="text-sm text-muted-foreground">Loading employee details...</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-800">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <>
              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Basic Information
                  </h3>
                  <DetailField label="Title" value={basicInfo?.title} />
                  <DetailField label="Full Name" value={basicInfo?.full_name} />
                  <DetailField label="Employee ID" value={basicInfo?.employee_id} />
                  <DetailField label="Employee Status" value={basicInfo?.employee_status} />
                  <DetailField label="Father's Name" value={basicInfo?.["father's_name"]} />
                  <DetailField label="Office Email" value={basicInfo?.office_email} />
                  <DetailField label="Personal Email" value={basicInfo?.personal_email} />
                  <DetailField label="Date of Birth" value={formatDate(basicInfo?.date_of_birth)} />
                  <DetailField label="Office Mobile" value={basicInfo?.office_mobile_number} />
                  <DetailField label="Personal Mobile" value={basicInfo?.personal_mobile_number} />
                  <DetailField label="Emergency Contact" value={basicInfo?.emergency_contact} />
                </div>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Employment Information
                  </h3>
                  <DetailField label="Designation" value={employmentInfo?.designation} />
                  <DetailField label="Department" value={employmentInfo?.department} />
                  <DetailField label="Reporting To" value={employmentInfo?.reporting_to} />
                  <DetailField label="Branch" value={employmentInfo?.branch} />
                  <DetailField label="Employment Type" value={employmentInfo?.employment_type} />
                  <DetailField label="Date of Joining" value={formatDate(employmentInfo?.date_of_joining)} />
                  <DetailField label="Date of Exit" value={formatDate(employmentInfo?.date_of_exit)} />
                  <DetailField label="Check In Time" value={employmentInfo?.check_in_time} />
                  <DetailField label="Check Out Time" value={employmentInfo?.check_out_time} />
                </div>
              </section>

              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Personal Information
                  </h3>
                  <DetailField label="Gender" value={personalInfo?.gender} />
                  <DetailField label="Marital Status" value={personalInfo?.marital_status} />
                  <DetailField label="Blood Group" value={personalInfo?.blood_group} />
                  <DetailField label="Religion" value={personalInfo?.relegion} />
                  <DetailField label="Aadhar Number" value={personalInfo?.aadhar_number} />
                  <DetailField label="PAN" value={personalInfo?.pan} />
                  <DetailField label="UAN" value={personalInfo?.uan} />
                  <DetailField label="ESI" value={personalInfo?.ESI} />
                  <DetailField label="Basic Salary" value={personalInfo?.basic_salary ? `₹${personalInfo.basic_salary}` : 'N/A'} />
                  <DetailField label="Gross Salary" value={personalInfo?.gross_salary ? `₹${personalInfo.gross_salary}` : 'N/A'} />
                </div>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Banking Details
                  </h3>
                  <DetailField label="Account Number" value={bankingDetails?.account_number} />
                  <DetailField label="Account Holder Name" value={bankingDetails?.account_holder_name} />
                  <DetailField label="Bank Name" value={bankingDetails?.bank_name} />
                  <DetailField label="IFSC Code" value={bankingDetails?.ifsc_code} />
                  <DetailField label="Account Type" value={bankingDetails?.account_type} />
                  <DetailField label="Branch Name" value={bankingDetails?.branch_name} />
                </div>
              </section>

              <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Present Address
                  </h3>
                  <DetailField label="Address Line 1" value={presentAddress?.address_line1} />
                  <DetailField label="Address Line 2" value={presentAddress?.address_line_2} />
                  <DetailField label="City" value={presentAddress?.city} />
                  <DetailField label="Postal Code" value={presentAddress?.postal_code} />
                  <DetailField label="State" value={presentAddress?.state} />
                  <DetailField label="Country" value={presentAddress?.country} />
                </div>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Permanent Address
                  </h3>
                  <DetailField label="Address Line 1" value={permanentAddress?.address_line1} />
                  <DetailField label="Address Line 2" value={permanentAddress?.address_line_2} />
                  <DetailField label="City" value={permanentAddress?.city} />
                  <DetailField label="Postal Code" value={permanentAddress?.postal_code} />
                  <DetailField label="State" value={permanentAddress?.state} />
                  <DetailField label="Country" value={permanentAddress?.country} />
                </div>
              </section>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetailsModal;

